import _ from 'lodash';
import { TYPE_EVENT_OTHER } from '@/shared/config';
import { getEventEffectdByUpdatedEvent } from './eventChanges';

/**
 * Merges locally pending event edits and additions into the day's fetched event list for one time window. Patches matching entries by idEvent or id and marks them isChanged, appends changes and new events whose type is requested and whose span overlaps the window, and sweeps the result for events superseded by an overlapping edit, before the append when changes carry through days and after it otherwise.
 *
 * @param data - The fetched events for the window, mutated in place and returned.
 * @param eventChange - The locally edited events not yet saved.
 * @param eventNew - The locally added events not yet saved.
 * @param startTime - Start of the displayed window, compared as an epoch.
 * @param stopTime - Stop of the displayed window, compared as an epoch.
 * @param types - The event types currently requested, gating which pending events may be appended.
 * @param shouldApplyChangeThroughDays - Whether edits made on another day may be pulled into this window.
 * @returns The data array with pending changes and additions merged in.
 */
export const updateEventChangeAndNew = (
  data: any[],
  eventChange: any[],
  eventNew: any[],
  startTime: any,
  stopTime: any,
  types: any[],
  shouldApplyChangeThroughDays = false,
): any => {
  //* start and stop of date
  const momentStart = new Date(startTime).getTime();
  const momentStop = new Date(stopTime).getTime();
  _.forEach(eventChange, (d) => {
    const ele = _.find(data, (x) => x.idEvent === d.idEvent);
    const eStart = new Date(d.start).getTime();
    const eStop = new Date(d.stop).getTime();
    if (ele) {
      _.assign(ele, { ...d, isChanged: true });
    } else if (
      types.includes(d.type) &&
      ((eStart >= momentStart && eStart <= momentStop) ||
        (eStop >= momentStart && eStop <= momentStop) ||
        (eStart <= momentStart && eStop >= momentStop)) &&
      shouldApplyChangeThroughDays
    ) {
      data.push({ ...d, isChanged: true });
    }
  });
  if (shouldApplyChangeThroughDays) {
    _.forEach(data, (event) => {
      const { event: deletedEvent } = getEventEffectdByUpdatedEvent(event, eventNew, eventChange);
      if (deletedEvent) {
        _.assign(event, { ...deletedEvent });
      }
    });
  }
  _.forEach(eventNew, (d) => {
    const ele = _.find(data, (x) => x.id === d.id);
    const eStart = new Date(d.start).getTime();
    const eStop = new Date(d.stop).getTime();
    if (ele) {
      _.assign(ele, { ...d, isChanged: true });
    } else if (
      types.includes(d.type) &&
      ((eStart >= momentStart && eStart <= momentStop) ||
        (eStop >= momentStart && eStop <= momentStop))
    ) {
      //* inside start and stop of date
      data.push({ ...d, isNew: true });
    } else if (shouldApplyChangeThroughDays && eStart <= momentStart && eStop >= momentStop) {
      data.push({ ...d, isNew: true });
    }
  });
  if (!shouldApplyChangeThroughDays) {
    _.forEach(data, (event) => {
      const { event: deletedEvent } = getEventEffectdByUpdatedEvent(event, eventNew, eventChange);
      if (deletedEvent) {
        _.assign(event, { ...deletedEvent });
      }
    });
  }
  return data;
};

/**
 * Retypes the ECG event behind the selected strip and produces the updated pending-change list, pending-new list and selected strip. Deep-clones all three inputs, then either drops the change when the picked type equals the currently active type, patches the matching entry in the new or changed list with type and oldType, or pushes a freshly typed clone into the change list.
 *
 * @param type - The picked event type option, read through its value field.
 * @param activeButton - The event type currently active in the tab, treated as the event's previous type.
 * @param selectedStrip - The event backing the selected strip, matched by id and returned as a retyped clone.
 * @param eventNews - The locally added events not yet saved.
 * @param eventChanges - The locally edited events not yet saved.
 * @returns An object carrying eChange, eNew and eSelect after the retype.
 */
export const updateEventChangeType = (
  type: any,
  activeButton: any,
  selectedStrip: any,
  eventNews: any[],
  eventChanges: any[],
) => {
  const eventType = type?.value;
  const cloneSelect = _.cloneDeep(selectedStrip);
  const cloneEvents = _.cloneDeep(eventChanges);
  const cloneEventNews = _.cloneDeep(eventNews);
  const eleEvent = _.find(cloneEvents, (x) => x.id === selectedStrip?.id);
  const eleEventNew = _.find(cloneEventNews, (x) => x.id === selectedStrip?.id);
  if (activeButton === eventType) {
    if (eleEvent) {
      _.remove(cloneEvents, (x) => x.id === eleEvent?.id);
      // delete eleEvent.type;
      delete cloneSelect.type;
      return { eChange: cloneEvents, eNew: [] as any[], eSelect: cloneSelect };
    }
    if (eleEventNew) {
      _.assign(eleEventNew, { type: eventType });
      return { eChange: [], eNew: cloneEventNews, eSelect: cloneSelect };
    }
    // case change same type event
    return { eChange: cloneEvents, eNew: [], eSelect: cloneSelect };
  }

  if (eleEvent) {
    _.assign(eleEvent, { type: eventType, oldType: activeButton });
    _.assign(cloneSelect, { type: eventType, oldType: activeButton });
    return { eChange: cloneEvents, eNew: [], eSelect: cloneSelect };
  }
  if (eleEventNew) {
    _.assign(eleEventNew, { type: eventType });
    _.assign(cloneSelect, { type: eventType });
    return { eChange: [], eNew: cloneEventNews, eSelect: cloneSelect };
  }
  const ele = _.assign(cloneSelect, { type: eventType, oldType: activeButton });
  _.assign(cloneSelect, { type: eventType, oldType: activeButton });
  cloneEvents.push(ele);
  return { eChange: cloneEvents, eNew: [], eSelect: cloneSelect };
};

export const handleAddEventOthersV2 = ({
  events = [] as any[],
  draw,
  allIndexStripSvg,
  technicianComments,
  captureTitle,
  startTime,
  endTime,
  miniteInfo,
}: {
  events?: any[];
  draw: any;
  allIndexStripSvg: any;
  technicianComments: any;
  captureTitle: any;
  startTime: any;
  endTime: any;
  miniteInfo: any;
}) => {
  try {
    const miniteData = _.cloneDeep(miniteInfo);
    const existSameTime = _.find(miniteData, (x) => x.start === startTime && x.stop === endTime);
    const data = _.cloneDeep(events);
    const ele = _.find(data, (x) => x.start === startTime && x.stop === endTime);
    if (ele) {
      _.assign(ele, {
        technicianComments,
        title: captureTitle,
        stripImgs: allIndexStripSvg,
        draw,
        isUpdatedOther: true,
        captures: [
          {
            ...(ele?.captures?.[0] || {}),
            draw,
            stripImgs: allIndexStripSvg,
            technicianComments,
            captureId: new Date().getTime(),
            isNewCapture: true,
            action: 'update',
          },
        ],
      });
    } else if (existSameTime) {
      _.assign(existSameTime, {
        technicianComments,
        title: captureTitle,
        stripImgs: allIndexStripSvg,
        draw,
        isUpdatedOther: true,
        captures: [
          {
            ...(existSameTime?.captures?.[0] || {}),
            draw,
            stripImgs: allIndexStripSvg,
            technicianComments,
            captureId: new Date().getTime(),
            isUpdateOtherImage: true,
            action: 'update',
          },
        ],
      });
      data.push(existSameTime);
    } else {
      data.push({
        start: startTime,
        stop: endTime,
        type: TYPE_EVENT_OTHER,
        technicianComments,
        title: captureTitle,
        isUpdatedOther: true,
        stripImgs: allIndexStripSvg,
        draw,
        id: new Date().getTime(),
        captures: [
          {
            draw,
            stripImgs: allIndexStripSvg,
            technicianComments,
            captureId: new Date().getTime(),
            isNewCapture: true,
            action: 'update',
          },
        ],
      });
    }
    return data;
  } catch (error) {
    throw new Error(error);
  }
};
